// src/components/Convocatoria.js - Versión Mejorada

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  Skeleton,
  Fab,
  Zoom,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  PictureAsPdf as PdfIcon, 
  Download as DownloadIcon,
  Share as ShareIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useCompetencia } from '../../context/CompetenciaContext';
import { useParams } from 'react-router-dom';
import jsPDF from 'jspdf';

// Componente para vista previa del PDF con controles mejorados
const PDFViewer = ({ url, title, onFullscreen, loading, error }) => {
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleIframeLoad = () => {
    setPdfLoading(false);
  };

  const handleIframeError = () => {
    setPdfLoading(false);
    setPdfError(true);
  };

  if (error || pdfError) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => window.open(url, '_blank')}>
            Abrir en nueva pestaña
          </Button>
        }
      >
        Error al cargar el PDF. Puedes intentar abrirlo en una nueva pestaña.
      </Alert>
    );
  }

  return (
    <Card sx={{ position: 'relative', height: isMobile ? '400px' : '600px' }}>
      <CardContent sx={{ p: 1, height: '100%' }}>
        <Box sx={{ 
          position: 'relative', 
          height: '100%', 
          borderRadius: 1, 
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }}>
          {/* Controles superiores */}
          <Box sx={{ 
            position: 'absolute', 
            top: 8, 
            right: 8, 
            zIndex: 2,
            display: 'flex',
            gap: 1,
            backgroundColor: 'rgba(255,255,255,0.9)',
            borderRadius: 1,
            p: 0.5
          }}>
            <Tooltip title="Vista completa">
              <IconButton size="small" onClick={onFullscreen}>
                <FullscreenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Abrir en nueva pestaña">
              <IconButton size="small" onClick={() => window.open(url, '_blank')}>
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Loader */}
          {(loading || pdfLoading) && (
            <Box sx={{ 
              position: 'absolute', 
              inset: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.9)',
              zIndex: 1,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary">
                Cargando documento PDF...
              </Typography>
            </Box>
          )}

          {/* PDF iframe */}
          <iframe
            src={url}
            width="100%"
            height="100%"
            title={title}
            style={{ 
              border: 'none',
              display: 'block',
              backgroundColor: 'white'
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Componente para vista de texto con mejor diseño
const TextViewer = ({ content, title }) => {
  return (
    <Card>
      <CardContent>
        <Box 
          className="ql-snow"
          sx={{ 
            '& .ql-editor': { 
              padding: '24px',
              backgroundColor: '#fafafa',
              borderRadius: 1,
              border: 'none',
              fontSize: '15px',
              lineHeight: 1.7,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: '1.5em',
                marginBottom: '0.5em',
                fontWeight: 600,
              },
              '& p': {
                marginBottom: '1em',
              },
              '& ul, & ol': {
                marginBottom: '1em',
                paddingLeft: '2em',
              },
              '& blockquote': {
                borderLeft: '4px solid #e0e0e0',
                paddingLeft: '16px',
                margin: '1em 0',
                fontStyle: 'italic',
                color: 'text.secondary',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }
            }
          }}
        >
          <div 
            className="ql-editor" 
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} 
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Componente principal mejorado
const Convocatoria = () => {
  const { slug } = useParams();
  const { competencias, loading: loadingCompetencias } = useCompetencia();
  const [loading, setLoading] = useState(false);
  const [competencia, setCompetencia] = useState(null);
  const [fullscreenPdf, setFullscreenPdf] = useState(false);
  const [error, setError] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Buscar competencia
  useEffect(() => {
    if (competencias && competencias.length > 0) {
      const foundCompetencia = competencias.find((comp) => comp.slug === slug);
      if (foundCompetencia) {
        setCompetencia(foundCompetencia);
        setError(null);
      } else {
        setError('Competencia no encontrada');
      }
    }
  }, [competencias, slug]);

  // Función de descarga mejorada
  const handleDownloadPDF = async () => {
    if (!competencia) return;
    
    setLoading(true);
    try {
      const convocatoria = competencia.convocatoria;
      
      // Priorizar PDF existente si está disponible
      if (convocatoria?.convocatoriaPDF) {
        const response = await fetch(convocatoria.convocatoriaPDF);
        const blob = await response.blob();
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `convocatoria_${competencia.general?.nombre || slug}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      } 
      // Solo generar PDF desde texto si no hay PDF disponible
      else if (convocatoria?.convocatoria) {
        const doc = new jsPDF('p', 'pt', 'a4');
        const tempDiv = document.createElement('div');
        
        // Estilos mejorados para la generación del PDF
        tempDiv.style.cssText = `
          padding: 60pt 40pt;
          font-family: 'Times New Roman', serif;
          font-size: 12pt;
          line-height: 1.6;
          max-width: 515pt;
          word-wrap: break-word;
          color: #333;
        `;
        
        // Agregar título si existe
        let htmlContent = '';
        if (competencia.general?.nombre) {
          htmlContent += `<h1 style="text-align: center; margin-bottom: 30pt; color: #1976d2;">${competencia.general.nombre}</h1>`;
          htmlContent += `<h2 style="text-align: center; margin-bottom: 40pt; color: #666;">CONVOCATORIA</h2>`;
        }
        htmlContent += DOMPurify.sanitize(convocatoria.convocatoria);
        
        tempDiv.innerHTML = htmlContent;
        document.body.appendChild(tempDiv);

        await doc.html(tempDiv, {
          callback: (pdf) => {
            const filename = `convocatoria_${competencia.general?.nombre || slug}.pdf`;
            pdf.save(filename.replace(/[^a-zA-Z0-9._-]/g, '_'));
            document.body.removeChild(tempDiv);
          },
          x: 40, 
          y: 40, 
          width: 515, 
          windowWidth: tempDiv.scrollWidth,
          html2canvas: { 
            scale: 0.8, 
            useCORS: true,
            logging: false,
            allowTaint: true
          },
          autoPaging: 'text'
        });
      }
    } catch (error) {
      console.error("Error al generar/descargar PDF:", error);
      setError("Error al descargar el documento");
    } finally {
      setLoading(false);
    }
  };

  // Función para compartir
  const handleShare = async () => {
    const url = window.location.href;
    const title = `Convocatoria - ${competencia?.general?.nombre || 'Competencia'}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          url,
          text: 'Consulta esta convocatoria'
        });
      } catch (error) {
        // Usuario canceló o error
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  // Función para copiar enlace
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      // Aquí podrías mostrar un snackbar de éxito
    } catch (error) {
      console.error('Error copiando enlace:', error);
    }
  };

  // Función para imprimir
  const handlePrint = () => {
    // Siempre priorizar PDF para impresión si está disponible
    if (competencia?.convocatoria?.convocatoriaPDF) {
      window.open(competencia.convocatoria.convocatoriaPDF, '_blank');
    } else {
      window.print();
    }
  };

  // Loading state
  if (loadingCompetencias) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1200px', mx: 'auto' }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        p: 4,
        textAlign: 'center'
      }}>
        <WarningIcon sx={{ fontSize: 80, color: 'warning.main', mb: 2 }} />
        <Typography variant="h4" color="text.primary" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          La convocatoria que buscas no está disponible o no existe.
        </Typography>
        <Button variant="contained" onClick={() => window.history.back()}>
          Volver atrás
        </Button>
      </Box>
    );
  }

  // No competencia found
  if (!competencia) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '60vh' 
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const convocatoria = competencia.convocatoria || {};
  const hasPdf = Boolean(convocatoria.convocatoriaPDF);
  const hasContent = Boolean(convocatoria.convocatoria?.trim());
  const hasAnyContent = hasPdf || hasContent;
  const displayPreference = convocatoria.displayPreference || 'auto';

  // Determinar qué mostrar basado en la preferencia
  const shouldShowPdf = () => {
    if (displayPreference === 'auto' && hasPdf) return true; // auto = solo PDF
    if (displayPreference === 'pdf' && hasPdf) return true; // pdf = PDF + texto
    if (displayPreference === 'text') return false;
    // Fallback: mostrar PDF si existe, sino texto
    return hasPdf;
  };

  const shouldShowText = () => {
    if (displayPreference === 'text' && hasContent) return true;
    if (displayPreference === 'pdf' && hasContent) return true; // pdf = PDF + texto
    if (displayPreference === 'auto') return false; // auto = solo PDF o solo texto
    // Fallback: mostrar texto solo si no hay PDF
    return hasContent && !hasPdf;
  };

  const showPdf = shouldShowPdf();
  const showText = shouldShowText();
  const showBothSections = displayPreference === 'pdf' && hasContent && hasPdf; // Mostrar texto como sección adicional cuando es modo "pdf"

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      py: { xs: 2, md: 4 }
    }}>
      <Box sx={{ 
        maxWidth: '1200px', 
        mx: 'auto', 
        px: { xs: 2, md: 4 }
      }}>
        {/* Header minimalista - solo botones de acción */}
        {hasAnyContent && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Descargar">
                <IconButton 
                  onClick={handleDownloadPDF}
                  disabled={loading}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                >
                  {loading ? <CircularProgress size={24} /> : <DownloadIcon />}
                </IconButton>
              </Tooltip>

              {/* Mostrar descarga de PDF específica si hay PDF pero se muestra texto */}
              {hasPdf && showText && displayPreference === 'text' && (
                <Tooltip title="Descargar PDF oficial">
                  <IconButton 
                    onClick={() => window.open(convocatoria.convocatoriaPDF, '_blank')}
                    color="secondary"
                    size={isMobile ? "small" : "medium"}
                  >
                    <PdfIcon />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="Compartir">
                <IconButton 
                  onClick={handleShare}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        )}

        {/* Content */}
        {hasAnyContent ? (
          <Box>
            {/* Mostrar alerta informativa si hay contenido oculto */}
            {hasPdf && hasContent && displayPreference === 'text' && (
              <Alert 
                severity="info" 
                sx={{ mb: 3 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    startIcon={<PdfIcon />}
                    onClick={() => window.open(convocatoria.convocatoriaPDF, '_blank')}
                  >
                    Ver PDF oficial
                  </Button>
                }
              >
                Se muestra la versión en texto. También hay disponible un PDF oficial.
              </Alert>
            )}

            {/* Contenido principal basado en displayPreference */}
            {showPdf && (
              <Box sx={{ mb: showBothSections ? 3 : 0 }}>
                <PDFViewer
                  url={convocatoria.convocatoriaPDF}
                  title="Convocatoria PDF"
                  onFullscreen={() => setFullscreenPdf(true)}
                  loading={loading}
                  error={error}
                />
              </Box>
            )}
            
            {showText && !showBothSections && (
              <Box sx={{ mb: 0 }}>
                {hasPdf && showText && displayPreference === 'text' && (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ArticleIcon />
                        Convocatoria
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PdfIcon />}
                        onClick={() => window.open(convocatoria.convocatoriaPDF, '_blank')}
                      >
                        Abrir PDF oficial
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                  </>
                )}
                <TextViewer 
                  content={convocatoria.convocatoria}
                  title="Convocatoria"
                />
              </Box>
            )}

            {/* Sección adicional cuando se muestra PDF + texto */}
            {showBothSections && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArticleIcon />
                  Información adicional
                </Typography>
                <TextViewer 
                  content={convocatoria.convocatoria}
                  title="Información adicional"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Card>
            <CardContent sx={{ 
              textAlign: 'center', 
              py: 8,
              backgroundColor: 'grey.50'
            }}>
              <ArticleIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
                Convocatoria no disponible
              </Typography>
              <Typography variant="body1" color="text.secondary">
                La convocatoria para esta competencia aún no ha sido publicada.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Floating Action Button for download on mobile */}
        {hasAnyContent && isMobile && (
          <Zoom in={true}>
            <Fab
              color="primary"
              aria-label="descargar"
              onClick={handleDownloadPDF}
              disabled={loading}
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
            </Fab>
          </Zoom>
        )}

        {/* Fullscreen PDF Modal */}
        {fullscreenPdf && hasPdf && (
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'black',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white'
            }}>
              <Typography variant="h6">
                Convocatoria - Vista completa
              </Typography>
              <IconButton
                onClick={() => setFullscreenPdf(false)}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1 }}>
              <iframe
                src={convocatoria.convocatoriaPDF}
                width="100%"
                height="100%"
                title="Convocatoria PDF - Vista completa"
                style={{ border: 'none' }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Convocatoria;